<template>
  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117] flex items-center justify-center px-4 py-10">
    <div class="w-full max-w-md bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">

      <!-- Invalid link -->
      <div v-if="!valid" class="px-6 py-10 text-center">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h1 class="text-base font-semibold text-gray-900 dark:text-white">This link is not valid</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5">
          Start again from the Timelydo extension by choosing Connect.
        </p>
        <RouterLink to="/integrations/job_tracker" data-turbo-prefetch="false"
          class="inline-flex px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">
          Go to Job Tracker
        </RouterLink>
      </div>

      <!-- Consent -->
      <template v-else>
        <div class="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-4">
          <img src="/images/integrations/job_tracker.png" alt="" class="w-12 h-12 rounded-xl flex-shrink-0">
          <div>
            <h1 class="text-lg font-bold text-gray-900 dark:text-white">Connect Timelydo Jobs</h1>
            <p class="text-sm text-gray-500 dark:text-gray-400">Allow this browser to use your account</p>
          </div>
        </div>

        <div class="px-6 py-5 space-y-4">
          <p class="text-sm text-gray-700 dark:text-gray-200">
            The Timelydo Jobs extension in <span class="font-semibold">{{ deviceName }}</span> wants to connect to
            <span class="font-semibold">{{ email }}</span>.
          </p>

          <ul class="text-sm text-gray-600 dark:text-gray-300 space-y-2">
            <li class="flex gap-2">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
              It can save jobs you find on job sites to your account.
            </li>
            <li class="flex gap-2">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-green-500" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
              It can tell which account it is connected to.
            </li>
            <li class="flex gap-2">
              <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
              It cannot use your password or the rest of your account.
            </li>
          </ul>

          <p class="text-xs text-gray-400 dark:text-gray-500">
            You can disconnect it at any time from Integrations, then Job Tracker.
          </p>

          <p v-if="error" class="text-sm text-red-500 dark:text-red-400">{{ error }}</p>
        </div>

        <div class="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex gap-3 justify-end">
          <button type="button" @click="respond('/extension/authorize/deny')" :disabled="submitting"
            class="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60">
            Cancel
          </button>
          <button type="button" @click="respond('/extension/authorize')" :disabled="submitting"
            class="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm disabled:opacity-60">
            {{ submitting ? 'Connecting...' : 'Allow' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useGlobalStore } from 'stores/global.js'
import { useRequest } from 'composables/useRequest.js'

const route = useRoute()
const globalStore = useGlobalStore()
const { axiosRequest } = useRequest()

const redirectUri = computed(() => String(route.query.redirect_uri || ''))
const state = computed(() => String(route.query.state || ''))
const codeChallenge = computed(() => String(route.query.code_challenge || ''))
const deviceName = computed(() => String(route.query.device_name || 'your browser').slice(0, 80))
const email = computed(() => globalStore.user.email)

// The server validates all of this again. Checking here only avoids showing a consent
// screen that can never succeed.
const valid = computed(() => Boolean(redirectUri.value && codeChallenge.value && state.value))

const submitting = ref(false)
const error = ref(null)

// Both actions answer with the address the window goes to next: the extension itself,
// carrying either a one time code or an access_denied error. The browser window that
// launchWebAuthFlow opened closes when it reaches that address.
async function respond(path) {
  submitting.value = true
  error.value = null

  const res = await axiosRequest('POST', path, {
    redirect_uri: redirectUri.value,
    state: state.value,
    code_challenge: codeChallenge.value,
  }, false)

  if (!res.js_error && res.response?.success) {
    window.location.href = res.response.data.redirect_to
    return
  }

  error.value = res.response?.message || 'Something went wrong. Please try again.'
  submitting.value = false
}
</script>
